import { useRef, useState } from "react";

import WalletModal from "components/Ui/WalletModal/WalletModal";
import WalletPanel from "components/Ui/WalletPanel/WalletPanel";
import { useGetDriverWallet, useTopUpDriverWallet, useDeductDriverWallet } from "hooks/useUser";
import { formatMoney } from "utils/money";

/**
 * محفظة السائق — the balance, a top-up or a deduction, and the ledger behind them.
 *
 * THE SAME FRAME AND PANEL AS THE RESTAURANT WALLET (admin#41): one component
 * draws both, so the two screens cannot drift. This file gives it only the
 * driver's data and words.
 *
 * THE HISTORY IS THE POINT. Every row names its reason, its amount and the
 * balance it left behind, so the number can be defended line by line. The
 * balance is the server's ledger SUM, not the cached `wallet_balance` column.
 */

/** What each driver movement is called. A negative adjustment reads «خصم». */
const DRIVER_TYPE_LABELS = {
    topup: "شحن رصيد",
    order_debit: "رسوم طلب",
    refund: "استرجاع رسوم",
    adjustment: "تسوية",
};

/** A retry key, made once per attempt. */
const newIdempotencyKey = () =>
    globalThis.crypto?.randomUUID?.() ??
    `wallet-${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * The notices, in the order they matter: a driver who cannot take orders
 * first, then the payment mode.
 *
 * @param {boolean} onWallet
 * @param {number|null} balance
 */
const buildDriverNotices = (onWallet, balance) => {
    if (balance === null) return [];

    if (!onWallet) {
        return [
            {
                tone: "info",
                text: "هذا السائق على النظام النقدي: الرسوم تُحسب في آخر الشهر، ولا تُخصم من المحفظة.",
            },
        ];
    }

    if (Number(balance) <= 0) {
        return [
            {
                tone: "danger",
                title: "لا يستطيع السائق استلام الطلبات",
                text: "رصيد المحفظة لا يكفي لرسوم الطلب. اشحن المحفظة ليستلم الطلبات من جديد.",
            },
        ];
    }

    return [];
};

const DriverWallet = (props) => {
    const [page, setPage] = useState(1);
    const driverUserId = props.user?.id;
    const onWallet = props.user?.delivery?.wallet_enabled === true;
    // THE SAME KEY FOR EVERY TRY OF ONE MOVEMENT. If the response is lost and
    // the operator presses again, the server sees the same key and moves the
    // money once. A new key is made only after the server confirmed.
    const idempotencyKey = useRef(newIdempotencyKey());

    const { balance, transactions, pages, loading, isFetching, error, refetch } =
        useGetDriverWallet(props.show ? driverUserId : null, page);
    const topUp = useTopUpDriverWallet();
    const deduct = useDeductDriverWallet();

    /**
     * @param {typeof topUp} mutation
     * @param {{done: string, failed: string}} words
     */
    const move =
        (mutation, words) =>
        async ({ amount, note }) => {
            try {
                const result = await mutation.mutateAsync({
                    userId: driverUserId,
                    amount,
                    note,
                    idempotencyKey: idempotencyKey.current,
                });
                idempotencyKey.current = newIdempotencyKey();
                props.showMessage(
                    "success",
                    result.duplicate ? "تمت معالجة العملية سابقاً" : words.done,
                    `الرصيد الآن ${formatMoney(result.balance)}`,
                );
                return true;
            } catch (err) {
                // The key is kept: a retry of THIS movement must reuse it. The
                // server's own message says WHY.
                props.showMessage(
                    "error",
                    words.failed,
                    err.response?.data?.message || "حدث خطأ غير متوقع",
                );
                return false;
            }
        };

    return (
        <WalletModal
            show={props.show}
            close={props.close}
            title={`محفظة ${props.user?.name || "السائق"}`}
        >
            <WalletPanel
                balance={balance}
                notices={buildDriverNotices(onWallet, balance)}
                transactions={transactions}
                typeLabels={DRIVER_TYPE_LABELS}
                page={page}
                pages={pages}
                onPageChange={setPage}
                loading={loading}
                isFetching={isFetching}
                error={error}
                onRetry={refetch}
                canTopUp
                onTopUp={move(topUp, { done: "تم الشحن", failed: "تعذر الشحن" })}
                onDeduct={move(deduct, { done: "تم الخصم", failed: "تعذر الخصم" })}
                topUpPending={topUp.isPending || deduct.isPending}
                zeroWarning={
                    onWallet
                        ? "بعد هذا الخصم يصبح الرصيد صفراً أو أقل، ولن يستطيع السائق استلام الطلبات حتى تُشحن محفظته."
                        : ""
                }
                showMessage={props.showMessage}
                linkOrders
            />
        </WalletModal>
    );
};

export default DriverWallet;
